import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger 
} from '../components/ui/dialog';
import { 
    Gift, 
    Plus, 
    Pencil, 
    Trash2,
    Loader2,
    Upload,
    ImageIcon,
    X,
    FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return `${process.env.REACT_APP_BACKEND_URL}${image}`;
};

export const AdminRewardsPage = () => {
    const { token } = useAuth();
    const { tenant } = useTenant();
    const fileInputRef = useRef(null);
    
    const [rewards, setRewards] = useState([]);
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingReward, setEditingReward] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [filterSection, setFilterSection] = useState('all');
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        points_required: '',
        active: true,
        section_id: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [rewardsRes, sectionsRes] = await Promise.all([
                axios.get(`${API}/rewards`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/admin/reward-sections`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setRewards(rewardsRes.data);
            setSections(sectionsRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Daten konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const fetchRewards = async () => {
        try {
            const response = await axios.get(`${API}/rewards`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRewards(response.data);
        } catch (error) {
            console.error('Failed to fetch rewards:', error);
        }
    };

    const openCreateDialog = () => {
        setEditingReward(null);
        setFormData({
            title: '',
            description: '',
            points_required: '',
            active: true,
            section_id: ''
        });
        setDialogOpen(true);
    };

    const openEditDialog = (reward) => {
        setEditingReward(reward);
        setFormData({
            title: reward.title,
            description: reward.description || '',
            points_required: reward.points_required.toString(),
            active: reward.active,
            section_id: reward.section_id || ''
        });
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title || !formData.points_required) {
            toast.error('Bitte füllen Sie alle Pflichtfelder aus');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                points_required: parseInt(formData.points_required),
                section_id: formData.section_id || null
            };

            if (editingReward) {
                await axios.put(
                    `${API}/admin/rewards/${editingReward.id}`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Prämie aktualisiert');
            } else {
                await axios.post(
                    `${API}/admin/rewards`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Prämie erstellt');
            }
            
            setDialogOpen(false);
            fetchRewards();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSubmitting(false);
        }
    };

    const getSectionName = (sectionId) => {
        const section = sections.find(s => s.id === sectionId);
        return section?.name || 'Keine Kategorie';
    };

    const filteredRewards = filterSection === 'all' 
        ? rewards 
        : filterSection === 'none' 
            ? rewards.filter(r => !r.section_id)
            : rewards.filter(r => r.section_id === filterSection);

    const handleImageUpload = async (rewardId, file) => {
        if (!file) return;
        
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Nicht unterstütztes Dateiformat');
            return;
        }
        
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Datei ist zu groß (max. 5MB)');
            return;
        }
        
        setUploadingImage(true);
        
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            
            await axios.post(
                `${API}/admin/rewards/${rewardId}/upload-image`,
                formDataUpload,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    } 
                }
            );
            
            toast.success('Bild hochgeladen');
            fetchRewards();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Upload fehlgeschlagen');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleDeleteImage = async (rewardId) => {
        try {
            await axios.delete(`${API}/admin/rewards/${rewardId}/image`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Bild gelöscht');
            fetchRewards();
        } catch (error) {
            toast.error('Bild konnte nicht gelöscht werden');
        }
    };

    const handleDelete = async (rewardId) => {
        try {
            await axios.delete(`${API}/admin/rewards/${rewardId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Prämie gelöscht');
            setDeleteId(null);
            fetchRewards();
        } catch (error) {
            toast.error('Prämie konnte nicht gelöscht werden');
        }
    };

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900">Prämien verwalten</h1>
                        <p className="text-slate-600 mt-1">
                            Erstellen und verwalten Sie Prämien für Ihr Empfehlungsprogramm
                        </p>
                    </div>
                    <Button
                        style={{ backgroundColor: tenant?.primary_color }}
                        onClick={openCreateDialog}
                        data-testid="create-reward-btn"
                    >
                        <Plus size={18} className="mr-2" />
                        Neue Prämie
                    </Button>
                </div>

                {/* Filter Bar */}
                {sections.length > 0 && (
                    <div className="flex items-center gap-4">
                        <Label className="text-slate-600">Kategorie:</Label>
                        <Select value={filterSection} onValueChange={setFilterSection}>
                            <SelectTrigger className="w-[200px]" data-testid="filter-section-select">
                                <SelectValue placeholder="Alle Kategorien" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Alle Kategorien</SelectItem>
                                <SelectItem value="none">Ohne Kategorie</SelectItem>
                                {sections.map(section => (
                                    <SelectItem key={section.id} value={section.id}>
                                        {section.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <span className="text-sm text-slate-500">
                            {filteredRewards.length} Prämie{filteredRewards.length !== 1 ? 'n' : ''}
                        </span>
                    </div>
                )}

                {/* Rewards List */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <Card key={i} className="animate-pulse">
                                <CardContent className="p-4">
                                    <div className="w-full h-40 bg-slate-200 rounded-xl mb-4" />
                                    <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
                                    <div className="h-4 bg-slate-200 rounded w-1/2" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : rewards.length === 0 ? (
                    <Card>
                        <CardContent className="p-12 text-center">
                            <Gift size={64} className="mx-auto text-slate-300 mb-4" />
                            <h3 className="text-xl font-semibold text-slate-900 mb-2">
                                Noch keine Prämien
                            </h3>
                            <p className="text-slate-500 mb-6">
                                Erstellen Sie Ihre erste Prämie
                            </p>
                            <Button
                                style={{ backgroundColor: tenant?.primary_color }}
                                onClick={openCreateDialog}
                            >
                                <Plus size={18} className="mr-2" />
                                Prämie erstellen
                            </Button>
                        </CardContent>
                    </Card>
                ) : filteredRewards.length === 0 ? (
                    <Card>
                        <CardContent className="p-12 text-center">
                            <FolderOpen size={64} className="mx-auto text-slate-300 mb-4" />
                            <h3 className="text-xl font-semibold text-slate-900 mb-2">
                                Keine Prämien in dieser Kategorie
                            </h3>
                            <p className="text-slate-500">
                                Wählen Sie eine andere Kategorie oder erstellen Sie eine neue Prämie
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="rewards-list">
                        {filteredRewards.map((reward) => (
                            <Card key={reward.id} className="card-hover overflow-hidden" data-testid={`reward-card-${reward.id}`}>
                                <CardContent className="p-0">
                                    {/* Image Section */}
                                    <div className="relative group">
                                        {reward.image ? (
                                            <div className="w-full h-40 bg-slate-100">
                                                <img 
                                                    src={getImageUrl(reward.image)} 
                                                    alt={reward.title}
                                                    className="w-full h-full object-cover"
                                                />
                                                {/* Overlay on hover */}
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        onClick={() => {
                                                            const input = document.createElement('input');
                                                            input.type = 'file';
                                                            input.accept = 'image/*';
                                                            input.onchange = (e) => handleImageUpload(reward.id, e.target.files[0]);
                                                            input.click();
                                                        }}
                                                    >
                                                        <Upload size={14} className="mr-1" />
                                                        Ändern
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="destructive"
                                                        onClick={() => handleDeleteImage(reward.id)}
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div 
                                                className="w-full h-40 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 transition-colors"
                                                style={{ backgroundColor: `${tenant?.primary_color}08` }}
                                                onClick={() => {
                                                    const input = document.createElement('input');
                                                    input.type = 'file';
                                                    input.accept = 'image/*';
                                                    input.onchange = (e) => handleImageUpload(reward.id, e.target.files[0]);
                                                    input.click();
                                                }}
                                            >
                                                {uploadingImage ? (
                                                    <Loader2 size={32} className="animate-spin text-slate-400" />
                                                ) : (
                                                    <>
                                                        <ImageIcon size={32} className="text-slate-300 mb-2" />
                                                        <span className="text-sm text-slate-400">Bild hinzufügen</span>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Status Badge */}
                                        <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                                            <Badge 
                                                variant={reward.active ? "default" : "secondary"}
                                                className={reward.active ? "bg-green-500" : ""}
                                            >
                                                {reward.active ? 'Aktiv' : 'Inaktiv'}
                                            </Badge>
                                            {reward.section_id && (
                                                <Badge variant="outline" className="bg-white/90 backdrop-blur-sm text-xs">
                                                    <FolderOpen size={10} className="mr-1" />
                                                    {getSectionName(reward.section_id)}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Content Section */}
                                    <div className="p-4">
                                        <h3 className="text-lg font-semibold text-slate-900 mb-1">
                                            {reward.title}
                                        </h3>
                                        {reward.description && (
                                            <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                                                {reward.description}
                                            </p>
                                        )}
                                        
                                        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                                            <span 
                                                className="text-lg font-bold"
                                                style={{ color: tenant?.primary_color }}
                                            >
                                                {reward.points_required.toLocaleString('de-DE')} Pkt.
                                            </span>
                                            <div className="flex gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openEditDialog(reward)}
                                                    data-testid={`edit-reward-${reward.id}`}
                                                >
                                                    <Pencil size={16} />
                                                </Button>
                                                <Dialog open={deleteId === reward.id} onOpenChange={(open) => !open && setDeleteId(null)}>
                                                    <DialogTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                            onClick={() => setDeleteId(reward.id)}
                                                            data-testid={`delete-reward-${reward.id}`}
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </DialogTrigger>
                                                    <DialogContent>
                                                        <DialogHeader>
                                                            <DialogTitle>Prämie löschen</DialogTitle>
                                                            <DialogDescription>
                                                                Möchten Sie "{reward.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                                                            </DialogDescription>
                                                        </DialogHeader>
                                                        <DialogFooter>
                                                            <Button variant="outline" onClick={() => setDeleteId(null)}>
                                                                Abbrechen
                                                            </Button>
                                                            <Button 
                                                                variant="destructive"
                                                                onClick={() => handleDelete(reward.id)}
                                                                data-testid="confirm-delete-btn"
                                                            >
                                                                Löschen
                                                            </Button>
                                                        </DialogFooter>
                                                    </DialogContent>
                                                </Dialog>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Create/Edit Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                {editingReward ? 'Prämie bearbeiten' : 'Neue Prämie'}
                            </DialogTitle>
                            <DialogDescription>
                                {editingReward 
                                    ? 'Aktualisieren Sie die Prämiendetails'
                                    : 'Fügen Sie eine neue Prämie hinzu'
                                }
                            </DialogDescription>
                        </DialogHeader>
                        
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">Titel *</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="z.B. Amazon Gutschein 25€"
                                    data-testid="reward-title-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="description">Beschreibung</Label>
                                <textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Beschreiben Sie die Prämie..."
                                    className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    data-testid="reward-description-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="points">Benötigte Punkte *</Label>
                                <Input
                                    id="points"
                                    type="number"
                                    min="1"
                                    value={formData.points_required}
                                    onChange={(e) => setFormData({ ...formData, points_required: e.target.value })}
                                    placeholder="z.B. 500"
                                    data-testid="reward-points-input"
                                />
                            </div>
                            
                            <div className="flex items-center justify-between">
                                <Label htmlFor="active">Aktiv</Label>
                                <Switch
                                    id="active"
                                    checked={formData.active}
                                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                                    data-testid="reward-active-switch"
                                />
                            </div>
                            
                            {sections.length > 0 && (
                                <div className="space-y-2">
                                    <Label htmlFor="section">Kategorie</Label>
                                    <Select 
                                        value={formData.section_id || 'none'} 
                                        onValueChange={(val) => setFormData({ ...formData, section_id: val === 'none' ? '' : val })}
                                    >
                                        <SelectTrigger data-testid="reward-section-select">
                                            <SelectValue placeholder="Kategorie wählen" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Keine Kategorie</SelectItem>
                                            {sections.map(section => (
                                                <SelectItem key={section.id} value={section.id}>
                                                    {section.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-slate-500">
                                        Prämien ohne Kategorie werden direkt auf der Hauptseite angezeigt
                                    </p>
                                </div>
                            )}
                            
                            {editingReward && (
                                <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-500">
                                    💡 Tipp: Sie können nach dem Speichern ein Bild hinzufügen, indem Sie auf die Bildplatzhalter-Fläche klicken.
                                </div>
                            )}
                            
                            <DialogFooter className="pt-4">
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    onClick={() => setDialogOpen(false)}
                                >
                                    Abbrechen
                                </Button>
                                <Button
                                    type="submit"
                                    style={{ backgroundColor: tenant?.primary_color }}
                                    disabled={submitting}
                                    data-testid="save-reward-btn"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Speichern...
                                        </>
                                    ) : (
                                        editingReward ? 'Aktualisieren' : 'Erstellen'
                                    )}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};
